import classNames from 'classnames';
import React from 'react';

import Button from 'sharedreact/components/Button';
import DropdownMenu from 'sharedreact/components/DropdownMenu';
import NavTab from 'sharedreact/components/NavTab';
import { translate } from 'sharedreact/utils/LanguageSupport';

type Tab = {
  id: string,
  name: string,
  className?: string
};

type Props = {
  listClassName?: string,
  title?: string,
  tabs?: Tab[],
  activeTab: number,
  noActiveTabName?: string,
  children?: React.ReactNode,
  hideIsoCollapse?: boolean,

  renderExtraContent?: () => React.ReactNode,
  onSelectTab: (tab: number) => void,
  onHideSidebar?: () => void
};

type DefaultProps = Required<Pick<Props, 'noActiveTabName'>>;

type PropsWithDefaults = Omit<Props, keyof DefaultProps> & DefaultProps;

class Sidebar extends React.PureComponent<Props> {
  static defaultProps: DefaultProps = {
    noActiveTabName: '???'
  };

  constructor (props: Props) {
    super(props);

    this.previousTab = this.previousTab.bind(this);
    this.nextTab = this.nextTab.bind(this);
  }

  isTabSelected (tabIndex: number): boolean {
    return (this.props.activeTab === tabIndex);
  }

  previousTab (): void {
    if (this.props.onSelectTab && this.props.activeTab > 0) {
      this.props.onSelectTab(this.props.activeTab - 1);
    }
  }

  nextTab (): void {
    if (this.props.tabs != null && this.props.onSelectTab && this.props.activeTab < this.props.tabs.length - 1) {
      this.props.onSelectTab(this.props.activeTab + 1);
    }
  }

  override render (): React.ReactNode {
    const { listClassName, onSelectTab, tabs, activeTab,
      noActiveTabName, hideIsoCollapse } = (this.props as PropsWithDefaults);

    if (tabs != null) {
      return (
        <div className='esko_fixedwidthcolumn esko_navigationcolumn esko_navigationcolumnseparation'>
          <div className='esko_visible-lg-inline esko_visible-md-inline esko_visible-sm-inline'>
            <div className='esko_sidebar_title_banner'>
              {this.props.title != null
              ? <h3 className='esko_nav-stacked-title'>{this.props.title}</h3>
              : null}
              {this.props.onHideSidebar != null
              ? <div className='esko_sidebar_title_middle' />
              : null
              }
              {this.props.onHideSidebar != null
              ? <Button id='ae_button_hide_sidebar' type='btn-link' className='esko_sidebar_button_hide' onClick={this.props.onHideSidebar}>{translate('shared.sidebar.hide')}</Button>
              : null
              }
            </div>
            <ul className={classNames('esko_nav esko_nav-pills esko_nav-pills-default esko_nav-stacked', listClassName)}>
              {tabs.map((tab: {id: string, name: string, className?: string, iconClassName?: string}, index: number) => {
                return <NavTab key={'tab_' + index} id={tab.id} tabIndex={index} label={tab.name} iconClassName={tab.iconClassName != null ? tab.iconClassName : ''} className={tab.className != null ? tab.className : ''} onSelect={onSelectTab} selected={this.isTabSelected(index)} />;
              })}
            </ul>
          </div>
          { hideIsoCollapse
            ? null
            : <div className='esko_visible-xs-inline'>
              <div className='esko_btn-group esko_btn-group-justified esko_collapsedsidebar' role='group' style={{tableLayout: 'auto'}}>
                <div className='esko_btn-group' role='group'>
                  <Button id='ae_button_previoustab' className='esko_sidebarbutton' icon='bi bi-caret-left-fill' onClick={this.previousTab} disabled={activeTab === 0} />
                </div>
                <div className='esko_btn-group' role='group' style={{width: '100%'}}>
                  <DropdownMenu id='ae_menu_selecttab' wrapperClassName='esko_sidebarmenudropdown' className='esko_sidebarbutton'
                    content={activeTab >= 0 && activeTab < tabs.length ? tabs[activeTab].name : noActiveTabName} centered>
                    {tabs.map((tab, index) => {
                      return (
                        <li key={'list_' + index}
                          onClick={(event) => onSelectTab(index)} className={classNames({active: index === activeTab}, tab.className != null ? tab.className : '')}>
                          <a id={tab.id} role='button'>{tab.name}</a>
                        </li>
                      );
                    })}
                  </DropdownMenu>
                </div>
                <div className='esko_btn-group' role='group'>
                  <Button id='ae_button_nexttab' className='esko_sidebarbutton' icon='bi bi-caret-right-fill' onClick={this.nextTab} disabled={activeTab >= tabs.length - 1} />
                </div>
              </div>
            </div>
          }
          {this.props.renderExtraContent != null ? this.props.renderExtraContent() : null}
        </div>
      );
    } else {
      return (
        <div className='esko_fixedwidthcolumn esko_navigationcolumnseparation'>
          <ul className='esko_nav esko_nav-pills esko_nav-pills-default esko_nav-stacked'>
            {this.props.children}
          </ul>
          {this.props.renderExtraContent != null ? this.props.renderExtraContent() : null}
        </div>
      );
    }
  }
}

export {
  Tab
};

export default Sidebar;
